import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, Clock } from "lucide-react";
import { formatTime } from "@/lib/date";
import { TID } from "@/lib/testIds";

/**
 * TodayCarousel — Streaming-catalog style carousel for "Hoy en Sur Primicia".
 *
 * Visual spec (compact):
 *  - Deep-blue petroleum background box, low padding.
 *  - No internal header — cards are the protagonists.
 *  - Bigger cards, generous rounding (rounded-3xl ≈ 24px).
 *  - Side arrows vertically centred over the scroller (desktop).
 *  - Dot indicators below.
 *  - Hover scales the card (transform only, no layout shift).
 *  - Mobile: 1 protagonist + peek; native swipe.
 *  - Respects prefers-reduced-motion.
 *
 * Behaviour (unchanged): consumes `block.articles` from the resolved layout,
 * routes to `/noticia/:slug` on click, and hides itself when empty.
 */
export default function TodayCarousel({ block }) {
  const items = block?.articles || [];

  const scrollerRef = useRef(null);
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(false);
  const [activeId, setActiveId] = useState(null);
  const rmRef = useRef(false);

  // Respect prefers-reduced-motion once on mount.
  useEffect(() => {
    if (typeof window !== "undefined" && window.matchMedia) {
      rmRef.current = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    }
  }, []);

  const updateArrows = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    setCanLeft(el.scrollLeft > 4);
    setCanRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  }, []);

  const updateActive = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const centre = el.scrollLeft + el.clientWidth / 2;
    let bestId = null;
    let bestDist = Infinity;
    el.querySelectorAll("[data-card-id]").forEach((n) => {
      const r = n.getBoundingClientRect();
      const scRect = el.getBoundingClientRect();
      const cx = r.left - scRect.left + r.width / 2 + el.scrollLeft;
      const dist = Math.abs(cx - centre);
      if (dist < bestDist) {
        bestDist = dist;
        bestId = n.getAttribute("data-card-id");
      }
    });
    if (bestId) setActiveId(bestId);
  }, []);

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    updateArrows();
    updateActive();
    const onScroll = () => { updateArrows(); updateActive(); };
    el.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", updateArrows);
    // Re-check dimensions once layout has fully settled — otherwise
    // clientWidth can be 0 on the very first tick and arrows stay hidden.
    const t1 = setTimeout(() => { updateArrows(); updateActive(); }, 60);
    const t2 = setTimeout(() => { updateArrows(); updateActive(); }, 300);
    let ro;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(() => { updateArrows(); updateActive(); });
      ro.observe(el);
    }
    return () => {
      el.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", updateArrows);
      clearTimeout(t1); clearTimeout(t2);
      if (ro) ro.disconnect();
    };
  }, [updateArrows, updateActive]);

  const scrollBy = (dir) => {
    const el = scrollerRef.current;
    if (!el) return;
    const first = el.querySelector("[data-card-id]");
    const step = first ? Math.round(first.getBoundingClientRect().width + 20) : el.clientWidth * 0.8;
    el.scrollBy({ left: dir * step, behavior: rmRef.current ? "auto" : "smooth" });
  };

  const scrollToIndex = (idx) => {
    const el = scrollerRef.current;
    if (!el) return;
    const cards = el.querySelectorAll("[data-card-id]");
    if (!cards[idx]) return;
    const target = cards[idx];
    el.scrollTo({ left: target.offsetLeft - 16, behavior: rmRef.current ? "auto" : "smooth" });
  };

  const onKey = (e) => {
    if (e.key === "ArrowRight") { e.preventDefault(); scrollBy(1); }
    else if (e.key === "ArrowLeft") { e.preventDefault(); scrollBy(-1); }
  };

  if (items.length === 0) return null;

  return (
    <section
      data-testid={TID.todayBlock || "home-today-block"}
      className="mb-14"
    >
      <div className="bg-[#051423] text-white py-6 sm:py-7 relative rounded-2xl overflow-hidden">
        {/* Scroller — vertical padding reserves room for the hover scale so
            NO layout shift happens when a card scales up. */}
        <div className="relative">
          <div
            ref={scrollerRef}
            role="region"
            aria-label="Noticias publicadas hoy"
            tabIndex={0}
            onKeyDown={onKey}
            className="today-scroller overflow-x-auto overflow-y-visible focus:outline-none focus-visible:ring-2 focus-visible:ring-turquoise"
            style={{
              scrollSnapType: "x proximity",
              WebkitOverflowScrolling: "touch",
              scrollbarWidth: "none",
              msOverflowStyle: "none",
              paddingTop: 14,
              paddingBottom: 14,
            }}
          >
            <ul className="flex gap-4 sm:gap-5 lg:gap-6 px-4 sm:px-8 lg:px-14 min-w-full w-max">
              {items.map((a) => {
                const isActive = activeId === a.id;
                const kicker = a.city_name || a.main_category_name || "";
                return (
                  <li
                    key={a.id}
                    data-card-id={a.id}
                    className="shrink-0 w-[82%] sm:w-[46%] md:w-[34%] lg:w-[26%] xl:w-[22%]"
                    style={{ scrollSnapAlign: "start", zIndex: isActive ? 5 : 1, position: "relative" }}
                  >
                    <Link
                      to={`/noticia/${a.slug}`}
                      data-testid={`today-card-${a.id}`}
                      className="today-card group relative block overflow-hidden rounded-3xl bg-blueblack aspect-[3/4] focus:outline-none focus-visible:ring-2 focus-visible:ring-turquoise"
                    >
                      <img
                        src={a.main_image}
                        alt={a.title}
                        loading="lazy"
                        decoding="async"
                        className="absolute inset-0 w-full h-full object-cover"
                        style={{ objectPosition: a.image_focal_point || "50% 40%" }}
                      />
                      {/* Bottom-only gradient — same rgba palette as Hero cards. */}
                      <div
                        className="absolute inset-x-0 bottom-0 h-[55%] pointer-events-none"
                        style={{
                          background:
                            "linear-gradient(to top, rgba(5,20,35,0.90) 0%, rgba(5,20,35,0.65) 35%, rgba(5,20,35,0.20) 65%, rgba(5,20,35,0) 100%)",
                        }}
                      />
                      <div className="absolute inset-x-0 bottom-0 p-4 sm:p-5 flex flex-col gap-1.5">
                        {kicker && (
                          <span
                            className="text-[10px] uppercase tracking-[0.16em] font-bold text-turquoise"
                            style={{ textShadow: "0 2px 6px rgba(5,20,35,0.55)" }}
                          >
                            {kicker}
                          </span>
                        )}
                        <h3
                          className="font-serif font-bold text-white text-base sm:text-lg leading-snug line-clamp-3"
                          style={{ textShadow: "0 3px 10px rgba(5,20,35,0.65), 0 1px 2px rgba(5,20,35,0.45)" }}
                        >
                          {a.title}
                        </h3>
                        <div
                          className="text-[10px] uppercase tracking-widest text-white/80 flex items-center gap-1.5 mt-0.5"
                          style={{ textShadow: "0 2px 5px rgba(5,20,35,0.55)" }}
                        >
                          <Clock className="w-3 h-3" />
                          {formatTime(a.published_at)} hs
                        </div>
                      </div>
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>

          {/* Side arrows — vertically centred over the scroller (desktop only).
              We keep the arrows always visible when items overflow, and only
              fade the currently-unreachable direction to 40% opacity so users
              can still see the affordance. */}
          <button
            type="button"
            onClick={() => scrollBy(-1)}
            disabled={!canLeft}
            aria-label="Anterior"
            data-testid="today-arrow-left"
            className={`hidden md:flex today-arrow today-arrow-left absolute z-20 left-3 lg:left-4 top-1/2 -translate-y-1/2 w-11 h-11 items-center justify-center rounded-full bg-black/70 hover:bg-black/85 text-white ring-1 ring-white/20 shadow-xl backdrop-blur-sm transition-opacity ${canLeft ? "opacity-100" : "opacity-30 pointer-events-none"}`}
          >
            <ChevronLeft className="w-6 h-6" />
          </button>
          <button
            type="button"
            onClick={() => scrollBy(1)}
            disabled={!canRight}
            aria-label="Siguiente"
            data-testid="today-arrow-right"
            className={`hidden md:flex today-arrow today-arrow-right absolute z-20 right-3 lg:right-4 top-1/2 -translate-y-1/2 w-11 h-11 items-center justify-center rounded-full bg-black/70 hover:bg-black/85 text-white ring-1 ring-white/20 shadow-xl backdrop-blur-sm transition-opacity ${canRight ? "opacity-100" : "opacity-30 pointer-events-none"}`}
          >
            <ChevronRight className="w-6 h-6" />
          </button>
        </div>

        {/* Dot indicators */}
        {items.length > 1 && (
          <div className="mt-3 flex items-center justify-center gap-1.5" aria-hidden="true">
            {items.map((a, i) => {
              const on = activeId === a.id || (!activeId && i === 0);
              return (
                <button
                  key={a.id}
                  type="button"
                  onClick={() => scrollToIndex(i)}
                  aria-label={`Ir a la noticia ${i + 1}`}
                  data-testid={`today-dot-${i}`}
                  className={`h-1.5 rounded-full transition-all ${on ? "w-5 bg-turquoise" : "w-1.5 bg-white/30 hover:bg-white/50"}`}
                />
              );
            })}
          </div>
        )}
      </div>

      <style>{`
        .today-scroller::-webkit-scrollbar { display: none; }
        .today-card {
          transition: transform 280ms ease, box-shadow 280ms ease;
          will-change: transform;
        }
        @media (hover: hover) and (pointer: fine) {
          .today-card:hover, .today-card:focus-visible {
            transform: scale(1.06);
            box-shadow: 0 18px 40px -12px rgba(5, 20, 35, 0.65);
          }
        }
        @media (hover: none) {
          li[data-card-id] { transition: transform 220ms ease; }
        }
        @media (prefers-reduced-motion: reduce) {
          .today-card { transition: none; }
          .today-card:hover, .today-card:focus-visible { transform: none; box-shadow: none; }
        }
      `}</style>
    </section>
  );
}
